import { useRef, useState } from "react";
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext";
import { useCampaign } from "../../contexts/useCampaign";
import { BatchCreatedRecord, BatchCreatedVideo, BatchCreateRecordInput, RecordsAPI } from "../../services/api";
import { BigFilesAPI, ChunkedUploadOptions } from "../../services/bigFiles";
import { VideoTypeEnum } from "../../types";
import { defaultSrid } from "../../utils/defaultValues";
import { GoProMetaDataReader, GoProMetadata } from "../../utils/goProMetaDataReader";
import { getVideoType, slugifyFileName, validateCampaignFiles } from "../../utils/videoAndRecord";
import { apiErrorMessage } from '../../utils/apiError';

export type HashStatus = 'new' | 'reuploading' | 'already_loaded';

interface VideoFile {
    file: File;
    type: VideoTypeEnum;
    lastModified: number;
    cameraId: string;
    hashId?: string;
    cameraModel?: string;
    cameraMode?: string;
    isCorrupted?: boolean;
    corruptionError?: string;
}

export interface RecordGroup {
    type: VideoTypeEnum;
    lastModifiedDate: Date;
    videos: VideoFile[];
    recordName: string;
    recordId?: number;
    hashStatus: HashStatus;
    existingRecordName?: string;
    existingRecordId?: number;
    existingVideoId?: number;
    reupload: boolean;
    // Videos created server-side by batch_create_records (carries the SAS upload URL), in videos order.
    createdVideos?: BatchCreatedVideo[];
}

export interface UploadItem {
    file: File;
    recordId: number;
    videoId?: number;
    status: "pending" | "uploading" | "completed" | "error";
    progress: number;
    error?: string;
    originalName: string;
    suggestedName: string;
    cameraId: string;
    hashId?: string;
    cameraModel?: string;
    cameraMode?: string;
    type: VideoTypeEnum;
    // Pre-created video upload target (from batch_create_records); when set, upload skips request_upload.
    uploadUrl?: string;
    blobName?: string;
    chunks?: {
        index: number;
        progress: number;
        status: "pending" | "uploading" | "completed" | "error";
    }[];
}

export const useBatchUpload = (
    onHide: () => void,
    onComplete: (result?: { success: boolean; message: string }) => void
) => {
    const [batchName, setBatchName] = useState<string>("");
    const [selectedVideoFiles, setSelectedVideoFiles] = useState<VideoFile[]>([]);
    const [recordGroups, setRecordGroups] = useState<RecordGroup[]>([]);
    const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const { setError: setGlobalError } = useError();
    const { setSuccess: setGlobalSuccess } = useSuccess();
    const { selectedCampaign, selectedNetwork } = useCampaign();
    const [batchSrid, setBatchSrid] = useState<number>(() => selectedNetwork?.srid ?? defaultSrid);
    const [step, setStep] = useState<"select" | "review" | "upload">("select");
    const [isCreatingRecords, setIsCreatingRecords] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Kept as a ref so the consumer can optionally attach it to a file input for reset
    const fileInputRef = useRef<HTMLInputElement>(null);

    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

    const resetState = () => {
        setBatchName("");
        setSelectedVideoFiles([]);
        setRecordGroups([]);
        setUploadQueue([]);
        setError(null);
        setStep("select");
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleFileSelect = (files: FileList) => {
        const validationError = validateCampaignFiles(Array.from(files), selectedCampaign?.type);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        const newVideoFiles: VideoFile[] = Array.from(files).map((file) => ({
            file,
            type: getVideoType(file.name),
            lastModified: file.lastModified,
            cameraId: "undefined",
        }));

        if (newVideoFiles.length === 0) {
            setSelectedVideoFiles(prevFiles => [...prevFiles]);
            return;
        }

        let pendingCallbacks = newVideoFiles.length;
        const allCallbacksCompleted = () => {
            pendingCallbacks--;
            if (pendingCallbacks === 0) {
                setSelectedVideoFiles(prevFiles => [...prevFiles, ...newVideoFiles]);
            }
        };

        newVideoFiles.forEach(videoFile => {
            const setCamIdCallback = (data: GoProMetadata | string) => {
                if (data === 'error' || !data) {
                    videoFile.isCorrupted = true;
                    videoFile.corruptionError = 'Erreur lors de la lecture du fichier';
                    videoFile.cameraId = "undefined";
                } else if (typeof data === 'object') {
                    videoFile.isCorrupted = false;
                    videoFile.cameraId = data.cameraId;
                    videoFile.hashId = selectedCampaign ? `${selectedCampaign.id}_${data.mediaID}` : data.mediaID;
                    videoFile.cameraModel = data.cameraModel || "unknown";
                    videoFile.cameraMode = data.cameraMode || "unknown";
                } else {
                    videoFile.isCorrupted = true;
                    videoFile.corruptionError = 'Format de données invalide';
                    videoFile.cameraId = "undefined";
                    videoFile.hashId = "undefined";
                }
                allCallbacksCompleted();
            };
            new GoProMetaDataReader(videoFile.file, setCamIdCallback);
        });
    };

    const groupVideos = async () => {
        if (!batchName.trim()) {
            setError("Please enter a batch name");
            return;
        }

        const corruptedVideos = selectedVideoFiles.filter(file => file.isCorrupted);
        if (corruptedVideos.length > 0) {
            const corruptedNames = corruptedVideos.map(file => file.file.name).join(', ');
            setError(`Cannot proceed: ${corruptedVideos.length} corrupted video(s) detected: ${corruptedNames}. Please remove corrupted videos before uploading.`);
            return;
        }

        const sortedFiles = [...selectedVideoFiles].sort((a, b) => a.lastModified - b.lastModified);

        const hashIdToFiles = new Map<string, string[]>();
        for (const f of sortedFiles) {
            if (f.hashId && f.hashId !== 'undefined') {
                const names = hashIdToFiles.get(f.hashId) ?? [];
                names.push(f.file.name);
                hashIdToFiles.set(f.hashId, names);
            }
        }
        const duplicates = [...hashIdToFiles.entries()].filter(([, names]) => names.length > 1);
        if (duplicates.length > 0) {
            const details = duplicates.map(([, names]) => names.join(' / ')).join('; ');
            setError(`Cannot proceed: duplicate media IDs detected — ${details}`);
            return;
        }

        const groups: RecordGroup[] = sortedFiles.map((videoFile, index) => ({
            type: videoFile.type,
            lastModifiedDate: new Date(videoFile.lastModified),
            videos: [videoFile],
            recordName: `${batchName} ${index + 1}`,
            hashStatus: 'new' as HashStatus,
            reupload: true,
        }));

        let enrichedGroups = groups;
        try {
            const hashIds = sortedFiles
                .map(f => f.hashId)
                .filter((id): id is string => !!id && id !== 'undefined');

            if (hashIds.length > 0) {
                const response = await RecordsAPI.checkHashIds(hashIds);
                const statusByHashId = new Map(response.data.results.map(r => [r.hash_id, r]));

                enrichedGroups = groups.map(group => {
                    const hashId = group.videos[0].hashId;
                    if (!hashId || hashId === 'undefined') return group;
                    const result = statusByHashId.get(hashId);
                    if (!result || !result.exists) return group;
                    return {
                        ...group,
                        hashStatus: (result.is_uploaded ? 'already_loaded' : 'reuploading') as HashStatus,
                        existingRecordName: result.record_name ?? undefined,
                        existingRecordId: result.record_id ?? undefined,
                        existingVideoId: result.video_id ?? undefined,
                    };
                });
            }
        } catch (err) {
            console.error('check_hash_ids failed, proceeding without status info:', err);
        }

        setRecordGroups(enrichedGroups);
        setError(null);
        setStep("review");
    };

    const setGroupReupload = (index: number, value: boolean) => {
        setRecordGroups(prev => prev.map((g, i) => i === index ? { ...g, reupload: value } : g));
    };

    const computeSuggestedName = (videoFile: VideoFile): string => {
        const formattedDate = new Date(videoFile.file.lastModified)
            .toISOString()
            .replace(/[-:]/g, '')
            .replace('T', '_')
            .slice(0, 15);

        const fileNameParts = videoFile.file.name.split('.');
        const extension = fileNameParts.pop()?.toLowerCase() || '';
        const baseName = fileNameParts.join('.');
        return slugifyFileName(`${formattedDate}_${baseName}.${extension}`);
    };

    const buildUploadItem = (
        videoFile: VideoFile,
        recordId: number,
        videoId?: number,
        uploadUrl?: string,
        blobName?: string,
    ): UploadItem => {
        const numChunks = Math.ceil(videoFile.file.size / CHUNK_SIZE);

        return {
            file: videoFile.file,
            recordId,
            videoId,
            uploadUrl,
            blobName,
            status: "pending",
            progress: 0,
            type: videoFile.type,
            originalName: videoFile.file.name,
            suggestedName: computeSuggestedName(videoFile),
            cameraId: videoFile.cameraId,
            hashId: videoFile.hashId,
            cameraModel: videoFile.cameraModel,
            cameraMode: videoFile.cameraMode,
            chunks: Array.from({ length: numChunks }, (_, i) => ({ index: i, progress: 0, status: "pending" as const })),
        };
    };

    const createRecordsAndUpload = async () => {
        if (recordGroups.length === 0) {
            setError("No record groups to create");
            return;
        }

        setIsCreatingRecords(true);

        try {
            if (!selectedCampaign) {
                setError("No campaign selected. Please select a campaign before uploading.");
                return;
            }

            const validationError = validateCampaignFiles(recordGroups.flatMap(group => group.videos.map(video => video.file)), selectedCampaign.type);
            if (validationError) {
                setError(validationError);
                return;
            }

            // Transactionally create all brand-new records and their linked videos in one call.
            // 'already_loaded' / 'reuploading' groups are handled as before and not sent.
            const newGroups = recordGroups.filter(group => group.hashStatus === 'new');
            let batchResults: BatchCreatedRecord[] = [];
            if (newGroups.length > 0) {
                const payload: BatchCreateRecordInput[] = newGroups.map(group => ({
                    name: group.recordName,
                    srid: batchSrid,
                    date_time: new Date().toISOString(),
                    videos: group.videos.map(videoFile => ({
                        title: computeSuggestedName(videoFile),
                        content_type: videoFile.file.type || "application/octet-stream",
                        file_size: videoFile.file.size,
                        hash_id: videoFile.hashId || "unknown",
                        camera_id: videoFile.cameraId,
                        camera_model: videoFile.cameraModel || "unknown",
                        camera_mode: videoFile.cameraMode || "unknown",
                        date_time: new Date(videoFile.file.lastModified).toISOString(),
                    })),
                }));
                const response = await RecordsAPI.batchCreateRecords(selectedCampaign.id, payload);
                batchResults = response.data.results;
            }

            // Results come back in the same order as newGroups; zip them back onto the groups.
            let newGroupIndex = 0;
            const resolvedGroups = recordGroups.map((group) => {
                if (group.hashStatus === 'already_loaded') {
                    return group;
                }
                if (group.hashStatus === 'reuploading') {
                    return { ...group, recordId: group.existingRecordId };
                }
                const created = batchResults[newGroupIndex++];
                return { ...group, recordId: created?.record_id, createdVideos: created?.videos };
            });

            setRecordGroups(resolvedGroups);

            const queue: UploadItem[] = resolvedGroups.flatMap((group) => {
                if (group.hashStatus === 'already_loaded') return [];
                if (group.hashStatus === 'reuploading' && !group.reupload) return [];
                if (!group.recordId) return [];
                if (group.hashStatus === 'reuploading') {
                    return group.videos.map(videoFile => buildUploadItem(videoFile, group.recordId!, group.existingVideoId));
                }
                // New groups: each video was pre-created with a SAS upload URL.
                return group.videos.map((videoFile, videoIndex) => {
                    const created = group.createdVideos?.[videoIndex];
                    return buildUploadItem(videoFile, group.recordId!, created?.video_id, created?.upload_url, created?.blob_name);
                });
            });

            setUploadQueue(queue);
            setStep("upload");
            setIsCreatingRecords(false);
            setIsUploading(true);
            await uploadFiles(queue, resolvedGroups.length);
        } catch (err) {
            setError(apiErrorMessage(err, 'Failed to create records'));
        } finally {
            setIsCreatingRecords(false);
        }
    };

    const updateItemProgress = (index: number, progress: number) => {
        setUploadQueue((queue) =>
            queue.map((item, i) => (i === index ? { ...item, progress } : item))
        );
    };

    const updateItemStatus = (
        index: number,
        status: UploadItem["status"],
        error?: string
    ) => {
        setUploadQueue((queue) =>
            queue.map((item, i) => (i === index ? { ...item, status, error } : item))
        );
    };

    const updateChunkProgress = (itemIndex: number, chunkIndex: number, progress: number) => {
        setUploadQueue((queue) => {
            const newQueue = [...queue];
            const item = { ...newQueue[itemIndex] };
            if (item.chunks) {
                const chunks = [...item.chunks];
                chunks[chunkIndex] = { ...chunks[chunkIndex], progress };
                item.chunks = chunks;
                item.progress = Math.round(chunks.reduce((sum, chunk) => sum + chunk.progress, 0) / chunks.length);
            }
            newQueue[itemIndex] = item;
            return newQueue;
        });
    };

    const updateChunkStatus = (
        itemIndex: number,
        chunkIndex: number,
        status: "pending" | "uploading" | "completed" | "error"
    ) => {
        setUploadQueue((queue) => {
            const newQueue = [...queue];
            const item = { ...newQueue[itemIndex] };
            if (item.chunks) {
                const chunks = [...item.chunks];
                chunks[chunkIndex] = { ...chunks[chunkIndex], status };
                item.chunks = chunks;
            }
            newQueue[itemIndex] = item;
            return newQueue;
        });
    };

    const uploadFile = async (item: UploadItem, index: number) => {
        try {
            updateItemStatus(index, "uploading");

            const chunkOptions: ChunkedUploadOptions = {
                onProgress: (progress) => updateItemProgress(index, progress),
                onChunkProgress: (chunkIndex, progress) => updateChunkProgress(index, chunkIndex, progress),
                onChunkComplete: (chunkIndex) => updateChunkStatus(index, chunkIndex, "completed"),
                onChunkError: (chunkIndex, err) => {
                    updateChunkStatus(index, chunkIndex, "error");
                    console.error(`Chunk ${chunkIndex} upload error:`, err);
                },
            };

            if (item.uploadUrl && item.blobName) {
                // Batch path: the video was pre-created with a SAS URL, upload straight to it.
                const hashId = item.hashId || "unknown";
                const result = await BigFilesAPI.uploadChunkedPreCreated(
                    item.recordId, hashId, item.uploadUrl, item.blobName, item.file, chunkOptions
                );
                if (result.success) {
                    updateItemStatus(index, "completed");
                    updateItemProgress(index, 100);
                    return true;
                }
                throw new Error(result.error || "Upload failed");
            }

            if (item.videoId !== undefined) {
                // Reupload path: upload to the existing video's blob, no record involved

                const result = await BigFilesAPI.uploadChunkedReupload(item.videoId, item.file, chunkOptions);
                if (result.success) {
                    updateItemStatus(index, "completed");
                    updateItemProgress(index, 100);
                    return true;
                }
                throw new Error(result.error || "Upload failed");

            }

            // New upload path
            const hashId = item.hashId || "unknown";
            const finalFileName = slugifyFileName(item.suggestedName);


            const result = await BigFilesAPI.uploadChunked(
                finalFileName, item.file, hashId,
                item.cameraId, chunkOptions, item.recordId, item.cameraModel, item.cameraMode
            );
            if (result.success) {
                updateItemStatus(index, "completed");
                updateItemProgress(index, 100);
                return true;
            }
            throw new Error(result.error || "Upload failed");

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Upload failed";
            updateItemStatus(index, "error", errorMessage);
            throw err;
        }
    };

    const uploadFiles = async (queue: UploadItem[], recordCount: number) => {
        try {
            const maxConcurrent = 3;
            let activeUploads = 0;
            let currentIndex = 0;
            const queueStatus: { [index: number]: "completed" | "error" } = {};

            return new Promise<void>((resolve) => {
                const processQueue = async () => {
                    if (currentIndex >= queue.length && activeUploads === 0) {
                        setIsUploading(false);
                        const successCount = Object.values(queueStatus).filter(s => s === "completed").length;
                        const errorCount = Object.values(queueStatus).filter(s => s === "error").length;

                        if (successCount > 0) {
                            const msg = `Batch upload complete: Created ${recordCount} records with ${successCount} file(s) uploaded successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`;
                            setGlobalSuccess(msg);
                            onComplete({ success: true, message: msg });
                        } else if (queue.length > 0) {
                            onComplete({ success: false, message: "No files were uploaded successfully" });
                        }

                        resolve();
                        return;
                    }

                    if (activeUploads < maxConcurrent && currentIndex < queue.length) {
                        const itemIndex = currentIndex++;
                        activeUploads++;

                        uploadFile(queue[itemIndex], itemIndex)
                            .then(() => { queueStatus[itemIndex] = "completed"; })
                            .catch(() => { queueStatus[itemIndex] = "error"; })
                            .finally(() => { activeUploads--; processQueue(); });

                        processQueue();
                    }
                };

                processQueue();
            });
        } catch (err) {
            setGlobalError("Failed to upload files: " + (err instanceof Error ? err.message : String(err)));
            setIsUploading(false);
        }
    };

    const removeFile = (index: number) => {
        setSelectedVideoFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const clearFiles = () => {
        setSelectedVideoFiles([]);
    };

    const clearError = () => {
        setError(null);
    };

    return {
        setStep,
        batchName,
        batchSrid,
        setBatchName,
        setBatchSrid,
        selectedVideoFiles,
        handleFileSelect,
        removeFile,
        clearFiles,
        groupVideos,
        recordGroups,
        setGroupReupload,
        createRecordsAndUpload,
        uploadQueue,
        error,
        clearError,
        step,
        isCreatingRecords,
        isUploading,
        resetState,
        fileInputRef,
    };
};
